import { BaseView } from "./base-view";
import { AssignmentDemandRole } from "../enums/AssignmentDemandRole";
import { Navigation } from "../navigation";
import { DemandDetailView } from "./demand-detail-view";
import { AssignmentDemand } from "../models/AssignmentDemand";
import { CellComponent, ColumnDefinition, RowComponent, Tabulator, TabulatorFull } from "tabulator-tables";
import { buildCalendarWeekId, closeModal, convertHtmlToNode, createEmployeePhotoElement, createNameIconElementWith2Initials, fireBaseLog, getCurrentCalendarWeekId, getEmployeeUtilizationByWeek, getWeekNumber, getWeekYear, percentageToColorWithInvert, percentageToGray, replaceAccentedCharacters, showToast } from "../common";
import { EmployeeUtilization } from "../models/EmployeeUtilization";
import { PageContext } from "../page-context";
import { ProjectAssignmentChange } from "../models/ProjectAssignmentChange";
import { Project } from "../models/Project";
import { Employee } from "../models/Employee";
import { CalendarWeek } from "../models/CalendarWeek";
import { globalApp } from "../app";
import { showResourceDetailModal } from "./resource-detail-view";


export class ResourceAssignmentView extends BaseView {
    static title = "Resource Assignments";
    static icon = "bi bi-file-binary";
    table!: Tabulator;
    filteredView: keyof typeof this.filteredViews = this.context.params.filteredVFiew || "all";

    currentCalendarWeekId: string;
    filteredViews = {
        "all": () => true,
        "me": (data: AssignmentDemand) => {
            if (data.assignments)
                return data.employee && data.employee.id === this.context.user.userId;
            return true;
        },
        "underutilized": (data: AssignmentDemand) => {
            if (!data.assignments)
                return true;

            //calculate median utilization for the next 4 weeks
            let employeeUtalisation = this.employeeUtilization.filter(utilisation => data.employee && utilisation.id == data.employee.id);
            if (data.employee && !data.employee.external && !data.employee.role?.startsWith("(Internal) ")) {
                let resultPercents = [];
                let currentDate = new Date(this.context.store.targetDate);
                for (let index = 0; index < 4; index++) {
                    let currentWeekUtalisation = 0;
                    let calendarWeekId = buildCalendarWeekId(getWeekNumber(currentDate));
                    if (data.assignments)
                        data.assignments.forEach(assignment => {
                            if (assignment.role != "Suggested Assignments") {
                                currentWeekUtalisation += assignment.plannedWeeks[calendarWeekId] ? assignment.plannedWeeks[calendarWeekId] : 0;
                            }
                        });

                    let [_, week, year] = calendarWeekId.split("_").map(Number);

                    let weekUtilisation = 40;
                    if (employeeUtalisation.length > 0) {
                        weekUtilisation = getEmployeeUtilizationByWeek(employeeUtalisation[0].utilization, week, year);
                    }

                    resultPercents.push(currentWeekUtalisation / weekUtilisation * 100);
                    currentDate.setDate(currentDate.getDate() + 7);
                }
                let medianPercent = 0;
                resultPercents.forEach(percent => medianPercent += percent);
                medianPercent /= resultPercents.length;

                return medianPercent < 60;
            }


            return false;
        },
        "my leadees": (data: AssignmentDemand) => {
            if (!data.assignments)
                return true;
            return data.employee && data.employee.leads.some(a => a === this.context.user.userId);
        }
    }
    employeeUtilization!: EmployeeUtilization[];
    unit: string;
    timescale: string;
    changedAssignmentsBefore: ProjectAssignmentChange[];
    changedAssignmentsAfter: AssignmentDemand[];
    lastModifiedCell: CellComponent | undefined
    assignments!: AssignmentDemand[];
    projectRoles!: string[];
    projectNames!: Project[];
    seniorities!: string[];
    locations!: string[];
    allEmployeesMap!: Map<string, Employee>;
    popstateHandler!: (event: any) => void;

    /**
   * constructor
   * @param {PageContext} context - The context of the page.
   * @param {string} [container=".main"] - The container to render the view in.
   */
    constructor(context: PageContext, container = ".main") {
        super(context, container);

        this.context.store.setTargetDate(new Date());
        this.unit = context.params.unit || context.userPreferences.getPreference("unit") || "hrs";
        this.timescale = context.params.timescale ? context.params.timescale : "week";
        this.changedAssignmentsBefore = [];
        this.changedAssignmentsAfter = [];
        this.lastModifiedCell = undefined;
        this.currentCalendarWeekId = getCurrentCalendarWeekId()

        let versionCheck = this.checkVersionAsync();

        Promise.all([versionCheck]).then(() => {
            let dataLoad = [
                this.context.store.getEmployeeAssignments(),
                this.context.store.getProjectRoles(),
                this.context.store.getProjects(),
                this.context.store.getSeniorities(),
                this.context.store.getLocations(),
                this.context.store.getAllEmployeesMap(),
                this.context.store.getEmployeesUtilization()
            ]

            Promise.all(dataLoad).then((values) => {
                this.assignments = values[0] as AssignmentDemand[];
                this.projectRoles = values[1] as string[];
                this.projectNames = values[2] as Project[];
                this.seniorities = values[3] as string[];
                this.locations = values[4] as string[];

                /** @type {Map<string, Employee>} */
                this.allEmployeesMap = values[5] as Map<string, Employee>;

                this.employeeUtilization = values[6] as EmployeeUtilization[];

                this.onDataLoaded();
            });
        });
    }

    connectedCallback(): this {
        super.connectedCallback();
        return this;
    }

    onDataLoaded() {

        this.innerHTML = `
        <div>
            <h1><i class="${ResourceAssignmentView.icon}"></i> Resource Assignments</h1>
            <p>
                View resource assignments by employee or project.
                <a class="help-page-link-icon" href="#/help#resource-assignments" title="Help page">
                    <i class="bi bi-info-circle"></i>
                </a>
            </p>
            
            
            <div class="row">
                <div class="col-auto me-auto table-toolbar-container">
                    <button accesskey="e" id="earlier-date" class="btn-datePick btn btn-sm btn-secondary" href="#" title="Move back by 2 months"><i class="bi bi-arrow-left"></i></button>
                    <button accesskey="t" id="datepicker" class="btn-datePick btn btn-sm btn-secondary" href="#" title="Select Date"><i class="bi bi-calendar-date"></i></button>
                    <button accesskey="l" id="later-date" class="btn-datePick btn btn-sm btn-secondary" href="#" title="Move forward by 2 months"><i class="bi bi-arrow-right"></i></button> 
                    <div id="unit" class="visually-hidden btn-group btn-group-sm btns-timescale px-2" role="group" aria-label="Timescale">
                        <button type="button" accesskey="w" class="btn btn-${this.timescale === "week" ? "" : "outline-"}secondary">week</button>
                        <button type="button" accesskey="m" class="btn btn-${this.timescale === "month" ? "" : "outline-"}secondary">month</button>
                    </div>

                    <button accesskey="r" class="btn btn-sm btn-outline-secondary btn-refresh" title="Reload data"><i class="bi bi-arrow-clockwise"></i></button>

                    <div class="btn-group btn-group-sm btn-filteredViews" role="group"></div>
                </div>

                <div class="col-auto table-toolbar-container">
                    <button accesskey="e" class="btn btn-sm btn-outline-secondary btn-export-csv" title="Export CSV"><i class="bi bi-cloud-arrow-down"></i></button>
                    <button accesskey="c" class="btn btn-sm btn-outline-secondary btn-toggle-cols" title="Show columns"><i class="bi bi-layout-three-columns"></i></button>
                    <div id="unit" class="btn-group btn-group-sm btns-units mx-2" role="group" aria-label="Units">
                        <button type="button" accesskey="h" class="btn btn-${this.unit === "hrs" ? "" : "outline-"}secondary" title="Hours">hrs</button>
                        <button type="button" accesskey="p" class="btn btn-${this.unit === "pd" ? "" : "outline-"}secondary" title="Project days">pd</button>
                        <button type="button" accesskey="z" class="btn btn-${this.unit === "%" ? "" : "outline-"}secondary" title="Percentage">%</button>
                    </div>
                </div>

            </div>
        </div>
        <div id="assignment-table" class="remaining-height"></div>`;

        const elem = $(this);

        elem.find(".btn-export-csv").click((e) => {
            this.table.download("csv", "export.csv");
        });

        elem.find(".btn-refresh").click((e) => {
            this.showLoading();
            this.context.store.assignmentChangeUnsubscribe(this.subscribedAssignmentChangeHandler);
            this.context.store.clearSubscribedAssignments().then((x) =>{
                this.context.store.getEmployeeAssignments().then((data) => {
                    this.assignments = data;
                    this.table.replaceData(data);
                    this.context.store.assignmentChangeSubscribe(this.subscribedAssignmentChangeHandler);
                    this.hideLoading();
                    this.calculateRowsForEachFilter();
    
                    this.changedAssignmentsBefore = [];
                    this.changedAssignmentsAfter = [];
                    this.updateSaveCancelButtons();
                    
                });
            });
        });

        elem.find(".btns-units").click((e) => {
            this.unit = $(e.target).text();
            $(".btns-units button").removeClass("btn-secondary").addClass("btn-outline-secondary");
            $(e.target).removeClass("btn-outline-secondary").addClass("btn-secondary");
            this.context.params.unit = this.unit;
            Navigation.navigateHash(null, this.context.params, true);
            this.context.userPreferences.setPreference("unit", this.unit);
            this.table.redraw(true);
        });

        elem.find(".btns-timescale").click((e) => {
            this.timescale = $(e.target).text();
            $(".btns-timescale button").removeClass("btn-secondary").addClass("btn-outline-secondary");
            $(e.target).removeClass("btn-outline-secondary").addClass("btn-secondary");
            this.context.params.timescale = this.timescale;
            Navigation.navigateHash(null, this.context.params, true);
            this.table.replaceData(this.table.getData());
            showToast("Not implemented yet", "Timescale changed to " + this.timescale);
        });

        const options = {
            dateFormat: "mm/dd/yy",
            todayHighlight: true,
            autoclose: true,
            calendarWeeks: true,
            todayBtn: true,
            weekStart: 1,
        };
        
        elem.find('#datepicker').datepicker(options).on('changeDate', (e) => {

            const selectedDate = $("#datepicker").datepicker("getDate");
            this.targetNewDate(selectedDate);
        });

        elem.find(`#earlier-date`).click((e) => {
            let currentTargetDate = this.context.store.targetDate;
            let newTargetDate = new Date(currentTargetDate.getFullYear(), currentTargetDate.getMonth() - 2, currentTargetDate.getDate());
            elem.find('#datepicker').datepicker('setDate', newTargetDate);
        });

        elem.find(`#later-date`).click((e) => {
            let currentTargetDate = this.context.store.targetDate;
            let newTargetDate = new Date(currentTargetDate.getFullYear(), currentTargetDate.getMonth() + 2, currentTargetDate.getDate());
            elem.find('#datepicker').datepicker('setDate', newTargetDate);
        });

        this.updateSaveCancelButtons();
        this.addTable();
        this.columnToggler($(".btn-toggle-cols")[0], this.table);

        const btnfilteredViews = elem.find(".btn-filteredViews");
        Object.keys(this.filteredViews).forEach((filterName) => {
            const filterButton = $(`<button class="btn btn-sm btn-${this.filteredView === filterName ? "" : "outline-"}secondary" data-view="${filterName}">${filterName}</button>`);
            filterButton.click((e) => {
                $(e.target).parent().children().removeClass("btn-secondary").addClass("btn-outline-secondary");
                $(e.target).removeClass("btn-outline-secondary").addClass("btn-secondary");
                this.filteredView = $(e.target).data("view");
                this.applyFilteredView();
            });
            btnfilteredViews.append(filterButton);
        });

        //cancel manually planned weeks on page change
        this.popstateHandler = (event) => {
            let hoverBox = document.getElementById('hoverBox');
            if (hoverBox && hoverBox.parentNode) {
                hoverBox.parentNode.removeChild(hoverBox);
            }
            if (this.changedAssignmentsAfter.length != 0) {
                this.cancelManuallyPlannedWeeks();
                window.removeEventListener('popstate', this.popstateHandler);
            };
        }
        window.addEventListener('popstate', this.popstateHandler);

        this.context.store.assignmentChangeUnsubscribeAll();
        this.context.store.assignmentChangeSubscribe(this.subscribedAssignmentChangeHandler);
    }

    targetNewDate(newTargetDate: Date) {
        this.showLoading();
        if (this.changedAssignmentsAfter.length != 0) {
            this.cancelManuallyPlannedWeeks();
            window.removeEventListener('popstate', this.popstateHandler);
        };
        this.enableDateButtons(false);
        this.context.store.setTargetDate(newTargetDate);
        this.context.store.getEmployeeAssignments(false).then((data) => {
            let currentFilters = this.table.getHeaderFilters();
            this.assignments = data;
            this.table.destroy();
            this.addTable();

            this.table.on("tableBuilt", () => {
                for (let filter of currentFilters) {
                    this.table.setHeaderFilterValue(filter.field, filter.value);
                }
            });

            this.enableDateButtons(true);
            this.hideLoading();
        });
    }

    enableDateButtons(value: boolean) {
        const elem = $(this);
        elem.find(`#earlier-date`).prop("disabled", !value);
        elem.find(`#later-date`).prop("disabled", !value);
    }

    updateSaveCancelButtons(cell: CellComponent | null = null) {
        let hoverBox = document.getElementById('hoverBox');
        if (this.changedAssignmentsAfter.length == 0 || !cell) {
            if (hoverBox)
                hoverBox.style.display = 'none';
            return;
        }

        if (hoverBox)
            hoverBox.style.display = 'block';
        else {
            hoverBox = document.createElement('div');
            hoverBox.id = 'hoverBox';
            hoverBox.style.cssText = 'position:absolute; background-color:white; padding:10px; border:1px solid black; z-index:1000; cursor:move; border-radius:10px';

            let createButton = (text: string, className: string, clickHandler: { (e: any): void; (this: HTMLButtonElement, ev: MouseEvent): any; }) => {
                let button = document.createElement('button');
                button.textContent = text;
                button.className = className;
                button.style.margin = '5px';
                button.addEventListener('click', clickHandler);
                if (hoverBox)
                    hoverBox.appendChild(button);
            };

            let buttons = [
                {
                    text: 'Apply till end',
                    className: 'btn btn-sm btn-primary btn-apply-till-end-manually-planned-week',
                    clickHandler: () => {
                        if (confirm("Are you sure you want to apply same utilisation till the end date for the last modified assignment?"))
                            this.applyTillEndLastAssignment();
                    }
                },
                {
                    text: 'Save',
                    className: 'btn btn-sm btn-primary btn-save-manually-planned-weeks',
                    clickHandler: () => {
                        let assignmentNamePlaceholder = '';
                        try {
                            assignmentNamePlaceholder = this.changedAssignmentsAfter.map(a => {
                                var validProject = this.projectNames.find((project) => project.id === a.projectId);
                                var validProjectName = '';
                                if (validProject)
                                    validProjectName = validProject.projectName;
                                return a.projectId + "-" + validProjectName;
                            }).join(",\n");
                        } catch (error) {
                            const err = error as Error;
                            if (err.name == "TypeError") { assignmentNamePlaceholder = this.changedAssignmentsAfter.map(a => a.projectId + "- Name Unassigned").join(",\n") };
                        }
                        if (!confirm("Are you sure you want to save calendar week planning for this assignments?\n" + assignmentNamePlaceholder)) {
                            showToast(`${assignmentNamePlaceholder}: Assignment save cancelled`, "success");
                            return;
                        }
                        this.saveManuallyPlannedWeeks();
                    }
                },
                {
                    text: 'Cancel',
                    className: 'btn btn-sm btn-danger btn-cancel-manually-planned-weeks',
                    clickHandler: () => {
                        let assignmentNamePlaceholder = '';
                        try {
                            assignmentNamePlaceholder = this.changedAssignmentsAfter.map(a => {
                                var validProject = this.projectNames.find((project) => project.id === a.projectId);
                                var validProjectName = '';
                                if (validProject)
                                    validProjectName = validProject.projectName;
                                return a.projectId + "-" + validProjectName;
                            }).join(",\n");
                        } catch (error) {
                            const err = error as Error;
                            if (err.name == "TypeError") { assignmentNamePlaceholder = this.changedAssignmentsAfter.map(a => a.projectId + " - Name Unassigned").join(",\n") };
                        }
                        if (!confirm("Are you sure you want to cancel calendar week planning for this assignments?\n" + assignmentNamePlaceholder)) {
                            showToast(`${assignmentNamePlaceholder}: Assignment suggest cancelled`, "success");
                            return;
                        }
                        this.cancelManuallyPlannedWeeks();
                    }
                }
            ];

            buttons.forEach(button => createButton(button.text, button.className, button.clickHandler));
            document.body.appendChild(hoverBox);

            var dragStartX: number, dragStartY: number;
            hoverBox.addEventListener('mousedown', (event) => {
                if (hoverBox) {
                    dragStartX = event.clientX - hoverBox.offsetLeft;
                    dragStartY = event.clientY - hoverBox.offsetTop;
                    document.addEventListener('mousemove', drag);
                    document.addEventListener('mouseup', endDrag);
                }
            });

            const drag = function (event: { clientX: number; clientY: number; }) {
                if (hoverBox) {
                    let newLeft = Math.min(Math.max(event.clientX - dragStartX, 0), window.innerWidth - hoverBox.offsetWidth - 30);
                    let newTop = Math.min(Math.max(event.clientY - dragStartY, 0), window.innerHeight - hoverBox.offsetHeight);
                    hoverBox.style.left = `${newLeft}px`;
                    hoverBox.style.top = `${newTop}px`;
                }
            }

            const endDrag = () => {
                document.removeEventListener('mousemove', drag);
                document.removeEventListener('mouseup', endDrag);
            }
        }

        let rect = cell.getElement().getBoundingClientRect();
        let hoverBoxWidth = hoverBox.offsetWidth;
        let hoverBoxHeight = hoverBox.offsetHeight;

        // Ensure the hoverBox does not go beyond the right edge of the screen + 30px to not colapse the hoverBox
        if (rect.left + hoverBoxWidth > window.innerWidth) {
            hoverBox.style.left = `${window.innerWidth - hoverBoxWidth - 40}px`;
        } else {
            hoverBox.style.left = `${rect.left}px`;
        }

        // Ensure the hoverBox does not go beyond the bottom edge of the screen
        if (rect.bottom + hoverBoxHeight > window.innerHeight) {
            hoverBox.style.top = `${window.innerHeight - hoverBoxHeight}px`;
        } else {
            hoverBox.style.top = `${rect.bottom}px`;
        }
    }

    async onRowClick(e: UIEvent, cell: CellComponent) {
        let row = cell.getRow();
        let data = row.getData();
        row.getTable().deselectRow();
        row.select();

        if (data.assignments && data.assignments.length > 0) {
            row.treeToggle();
            return;
        }

        const parentRow = row.getTreeParent();
        const parentRowData = parentRow ? parentRow.getData() : undefined;
        if (data.role === AssignmentDemandRole.AbsenceAssigmentRole || data.role === AssignmentDemandRole.SuggestionsAssignmentRole || parentRowData?.role === AssignmentDemandRole.SuggestionsAssignmentRole) {
            return;
        }

        Navigation.navigateHash("/resource-assignment/detail", { assignmentId: data.id }, true);
        var ctx = Object.assign(this.context, { data: data });
        await DemandDetailView.showAsModal(ctx);
        Navigation.restorePreviousHashUrl();
        
        this.changedAssignmentsBefore = [];
        this.changedAssignmentsAfter = [];
        this.updateSaveCancelButtons();
    }

    AbscenceOnTopSorter = (a: string | null, b: string | null, aRow: RowComponent, bRow: RowComponent, column: any, dir: string, sorterParams: any) => {
        if (aRow.getData().role == AssignmentDemandRole.SuggestionsAssignmentRole || bRow.getData().role == AssignmentDemandRole.SuggestionsAssignmentRole)
            return (aRow.getData().role == AssignmentDemandRole.SuggestionsAssignmentRole ? 1 : -1) * (dir == "asc" ? 1 : -1);
        if (aRow.getData().role == AssignmentDemandRole.AbsenceAssigmentRole || bRow.getData().role == AssignmentDemandRole.AbsenceAssigmentRole)
            return (aRow.getData().role == AssignmentDemandRole.AbsenceAssigmentRole ? -1 : 1) * (dir == "asc" ? 1 : -1);
        if (a === null)
            a = '';
        if (b === null)
            b = '';
        return a.toString().localeCompare(b.toString());
    };

    getColumns(): ColumnDefinition[] {

        const projectIds = Array.from(
            new Set(
                this.assignments
                    .flatMap((a) => a.assignments ? a.assignments.map((a2) => a2.projectId) : [])
                    .filter((projectId) => projectId && projectId.trim() !== "")
            )
        );

        let columns: ColumnDefinition[] = [
            {
                title: "Employee / Role",
                field: "role",
                frozen: true,
                width: 250,
                headerFilter: "input",
                headerFilterFunc: function (headerValue, rowValue, rowData) {
                    if (rowData.assignments) {
                        if (!rowValue || !headerValue) return false;
                        return replaceAccentedCharacters(rowValue.toLowerCase()).includes(replaceAccentedCharacters(headerValue.toLowerCase()));
                    }
                    return true;
                },
                formatter: this.namesWithIconFormatter,
                cellClick: this.onRowClick.bind(this),
                tooltip: true,
                sorter: this.AbscenceOnTopSorter,
            },
            {
                title: "Project",
                field: "projectId",
                frozen: true,
                width: 200,
                formatter: this.buttonFormatter,
                sorter: (a: any, b: any, aRow: RowComponent, bRow: RowComponent, column: any, dir: string, sorterParams: any) => {
                    const aData = aRow.getData() as AssignmentDemand;
                    const bData = bRow.getData() as AssignmentDemand;
                    if (aRow.getData().role == AssignmentDemandRole.SuggestionsAssignmentRole || bRow.getData().role == AssignmentDemandRole.SuggestionsAssignmentRole)
                        return (aRow.getData().role == AssignmentDemandRole.SuggestionsAssignmentRole ? 1 : -1) * (dir == "asc" ? 1 : -1);
                    if (aData.role == AssignmentDemandRole.AbsenceAssigmentRole || bData.role == AssignmentDemandRole.AbsenceAssigmentRole)
                        return (aData.role == AssignmentDemandRole.AbsenceAssigmentRole ? -1 : 1) * (dir == "asc" ? 1 : -1);
                    if (!aData.assignments || !bData.assignments)
                        return 0;
                    return aData.assignments.length - bData.assignments.length;
                },
                cellClick: this.onRowClick.bind(this),
                headerFilter: this.createMultiselectHeaderFilter(projectIds.sort()),
                headerFilterFunc: (selectedValues, _rowValue, rowData, _filterParams) => {
                    if (!rowData.assignments || !selectedValues || selectedValues.length === 0) {
                        return true;
                    }

                    const assignmentProjects: string[] = rowData.assignments.map((assignment: AssignmentDemand) => assignment.projectId);
                    return selectedValues.some((value: string) => assignmentProjects.includes(value));
                },
                vertAlign: "middle"
            },
            {
                title: "Originating project",
                field: "originatingProjectId",
                visible: false,
                frozen: true,
                width: 200,
                headerFilter: "input",
                headerFilterFunc: this.customOriginatingProjectFilter,
                tooltip: true
            },
            {
                title: "Role",
                field: "employee.role",
                width: 100,
                frozen: true,
                headerFilter: "list",
                headerFilterParams: {
                    values: this.projectRoles,
                },
                headerFilterFunc: function (headerValue, rowValue, rowData) {
                    if (rowData.assignments) {
                        return rowValue?.toLowerCase().includes(headerValue?.toLowerCase());
                    }
                    return true;
                },
                tooltip: true,
                vertAlign: "middle",
                visible: false,
                sorter: this.AbscenceOnTopSorter,
            },
            {
                title: "Seniority",
                field: "employee.seniority",
                width: 100,
                frozen: true,
                headerFilter: "list",
                headerFilterParams: {
                    values: this.seniorities,
                },
                headerFilterFunc: function (headerValue, rowValue, rowData) {
                    if (rowData.assignments) {
                        return rowValue?.toLowerCase().includes(headerValue?.toLowerCase());
                    }
                    return true;
                },
                vertAlign: "middle",
                tooltip: true,
                visible: false,
                sorter: this.AbscenceOnTopSorter,
            },
            {
                title: "Location",
                field: "employee.location",
                headerFilter: "list",
                headerFilterFunc: function (headerValue, rowValue, rowData) {
                    if (rowData.assignments) {
                        return rowValue?.toLowerCase().includes(headerValue?.toLowerCase());
                    }
                    return true;
                },
                headerFilterParams: {
                    values: this.locations,
                },
                width: 100,
                frozen: true,
                vertAlign: "middle",
                tooltip: true,
                visible: false,
                sorter: this.AbscenceOnTopSorter,
            }
        ];

        const cwColsFormatterParams = {
            maxHours: 40,
            employeesMap: this.allEmployeesMap
        };
        const cwColumns = this.addCalendarWeekCols(this.context.store.getCalendarWeeks(), this.context.user.hasEditPermission(), cwColsFormatterParams);
        return columns.concat(cwColumns);
    }


    addTable() {
        let columns = this.getColumns();
        this.columnToggleFromLocalStorage(columns);
        const table = new TabulatorFull("#assignment-table", {
            height: 200,
            layout: "fitData",
            data: this.assignments,
            dataTree: true,
            dataTreeStartExpanded: false,
            dataTreeChildField: "assignments",
            columns: columns,
            headerSortClickElement: "icon",
            initialSort: [
                { column: "role", dir: "asc" },
            ],
        });

        table.on("tableBuilt", async () => {
            this.applyFilteredView();

            // expand the row if we have an assignmentId in the context
            if (this.context.params.assignmentId) {
                for (const row of table.getRows()) {
                    const parentData = row.getData() as AssignmentDemand;
                    if (parentData.assignments)
                        parentData.assignments.forEach(async (a, idx) => {
                            if (a.id === this.context.params.assignmentId) {
                                row.treeExpand();
                                //TODO check if this was working?
                                //table.scrollToRow(row);
                                table.selectRow(row.getData().id);
                                var ctx = Object.assign(this.context, { data: a });
                                var result = await DemandDetailView.showAsModal(ctx);
                                Navigation.restorePreviousHashUrl();

                                if (result) {
                                    if (parentData.assignments)
                                        parentData.assignments[idx] = result;
                                    this.context.store.calculateTotalWeekly(parentData);
                                    row.update(parentData);
                                }
                            }
                        });
                };
            } else if (this.context.params.employeeId) {
                for (const row of table.getRows()) {
                    var parentData = row.getData();
                    if (parentData.employee.id === this.context.params.employeeId) {
                        row.treeExpand();
                        //TODO check if this was working?
                        //table.scrollToRow(row);
                        table.selectRow(row.getData().id);
                    }
                };
            } else if (this.context.params.employeeIds) {
                this.table.setFilter((data) => {
                    //include child rows in filter
                    if (!data.assignments)
                        return true;
                    return data.employee && this.context.params.employeeIds.includes(data.employee.id)});
                this.setInitialTableSort();
                this.resetTableScroll();
            } else {
                closeModal();
            }

            this.jumpToWeek();
            this.calculateRowsForEachFilter();
        });

        table.on("cellEditing", (cell) => {
            const row = cell.getRow();
            const data = row.getData();

            const parentRow = row.getTreeParent();
            if (parentRow) {
                let parentData = parentRow.getData();
                if (parentData.role === AssignmentDemandRole.SuggestionsAssignmentRole) {
                    cell.cancelEdit();
                    return;
                }
            }

            if (data.role == AssignmentDemandRole.AbsenceAssigmentRole || !this.isEditable(cell)) {
                cell.cancelEdit();
                return;
            }

            if (!this.changedAssignmentsBefore.find(a => a.id === data.id)) {
                let copy = new ProjectAssignmentChange({ id: data.id, manuallyPlanned: data.manuallyPlanned });
                for (let key in data.plannedWeeks) {
                    if (key.startsWith('cw_')) {
                        copy[key] = data.plannedWeeks[key];
                    }
                }
                this.changedAssignmentsBefore.push(copy);
            }
        });

        table.on("cellEdited", async (cell) => {
            const row = cell.getRow();
            const data = row.getData() as AssignmentDemand;

            if (!data.manuallyPlanned) {
                showToast(`You have started manually planning for assignment: ${data.projectId}`, "success");
            }

            data.manuallyPlanned = true;
            data.pdTotalPlanned = 0;
            [data.startDate, data.endDate] = [new Date(9999, 11, 31), new Date(0, 0, 1)];

            //calculate first monday and last friday
            Object.keys(data.plannedWeeks).filter(key => key.startsWith("cw_") && data.plannedWeeks[key] != 0).forEach(key => {
                data.pdTotalPlanned += data.plannedWeeks[key];
                const [_, weekNumber, year] = key.split("_");
                const [firstDay, lastDay] = [this.getUTCWeekDay(Number(year), Number(weekNumber), 1), this.getUTCWeekDay(Number(year), Number(weekNumber), 5)];
                data.startDate = data.startDate ? new Date(Math.min(data.startDate.getTime(), Number(firstDay))) : firstDay;
                data.endDate = data.endDate ? new Date(Math.max(data.endDate.getTime(), Number(lastDay))) : lastDay;
            });

            data.pdTotalPlanned = Math.ceil(data.pdTotalPlanned / 8);

            if (!this.changedAssignmentsAfter.find(a => a.id === data.id)) {
                this.changedAssignmentsAfter.push(data);
            }
            this.lastModifiedCell = cell;

            this.updateSaveCancelButtons(cell);

            const parentRow = row.getTreeParent();
            if (parentRow) {
                const parentData = parentRow.getData() as AssignmentDemand;
                this.context.store.calculateTotalWeekly(parentData);
                parentRow.update(parentData);
            }
        });

        this.table = table;
        return this;
    }

    createMultiselectHeaderFilter(items: string[]) {
        return (cell: CellComponent, onRendered: (arg0: () => void) => void, success: (arg0: string) => void, cancel: any) => {
            var input = document.createElement("select");
            input.style.width = "100%";
            input.style.height = "auto";
            input.setAttribute("multiple", "");

            items.forEach((item) => {
                input.add(new Option(item, item));
            });

            onRendered(() => {
                const $input = $(input);
                $input.select2({
                    closeOnSelect: false,
                    width: "100%",
                }).on("change", () => {
                    success($input.val() as string);
                    this.table.redraw(true);
                })
                    .on("select2:select", () => {
                        const $searchField = $input.data('select2').dropdown.$search || $input.data('select2').selection.$search;
                        if ($searchField) {
                            $searchField.val('').trigger('input');
                            $searchField.focus();
                        }
                    }).on('select2:unselect', () => {
                        const $searchField = $input.data('select2').dropdown.$search || $input.data('select2').selection.$search;
                        if ($searchField) {
                            $searchField.val('').trigger('input');
                            $searchField.focus();
                        }
                    });
            });

            return input;
        };
    }

    async applyTillEndLastAssignment() {
        if (this.changedAssignmentsAfter.length == 0 || !this.lastModifiedCell)
            return;

        let lastAssignment = this.changedAssignmentsAfter[this.changedAssignmentsAfter.length - 1];
        let [_, lastModifiedWeekNumber, lastModifiedWeekYear] = this.lastModifiedCell.getField().split("_").map(Number);
        let lastModifiedCellDate = this.getUTCWeekDay(lastModifiedWeekYear, lastModifiedWeekNumber, 1);
        let lastModifiedCellValue = this.lastModifiedCell.getValue();
        if (lastAssignment.endDate) {
            let endDateWeekNumber = getWeekNumber(lastAssignment.endDate);
            let endDateYear = getWeekYear(lastAssignment.endDate);
            let endDate = this.getUTCWeekDay(endDateYear, endDateWeekNumber, 5)


            lastAssignment.pdTotalPlanned = 0;

            Object.keys(lastAssignment.plannedWeeks).filter(key => key.startsWith("cw_")).forEach(key => {
                let [_, weekNumber, year] = key.split("_").map(Number);
                let currentDate = this.getUTCWeekDay(year, weekNumber, 5);

                if (lastModifiedCellDate <= currentDate && endDate >= currentDate) {
                    lastAssignment.plannedWeeks[key] = lastModifiedCellValue;
                    lastAssignment.pdTotalPlanned += lastAssignment.plannedWeeks[key];
                }
            });
        }

        lastAssignment.pdTotalPlanned = Math.ceil(lastAssignment.pdTotalPlanned / 8);

        let parentRow = this.lastModifiedCell.getRow().getTreeParent();
        if (parentRow) {
            let parentData = parentRow.getData() as AssignmentDemand;
            this.context.store.calculateTotalWeekly(parentData);
            parentRow.update(parentData);
        }
    }

    async saveManuallyPlannedWeeks() {
        this.showLoading();
        const updatePromises: Promise<AssignmentDemand>[] = [];
        this.changedAssignmentsAfter.forEach(assignment => {
            updatePromises.push(this.context.store.updateAssignment(assignment));
        });
        await Promise.all(updatePromises);

        this.changedAssignmentsAfter.map(assignment => assignment.id).forEach(assignmentId => {
            fireBaseLog('assignment_table_manual_cell_change', { assignmentId: assignmentId });
        });

        this.changedAssignmentsBefore = [];
        this.changedAssignmentsAfter = [];
        this.updateSaveCancelButtons();

        showToast("Assignment suggested", "success");
        this.hideLoading();
    }

    cancelManuallyPlannedWeeks() {
        this.showLoading();
        const oldValuesMap = new Map(this.changedAssignmentsBefore.map(a => [a.id, a]));

        this.assignments.forEach(assignmentContainer => {
            let rowUpdated = false;
            if (assignmentContainer.assignments)
                assignmentContainer.assignments.forEach(assignment => {
                    if (oldValuesMap.has(assignment.id)) {
                        Object.assign(assignment.plannedWeeks, oldValuesMap.get(assignment.id));
                        rowUpdated = true;
                    }
                });
            if (rowUpdated) {
                const rows = this.table.getRows();
                const row = rows.find(row => row.getData().role === assignmentContainer.role);
                if (row) {
                    row.update(assignmentContainer);
                    var updatedData = row.getData() as AssignmentDemand;
                    this.context.store.calculateTotalWeekly(updatedData);
                    row.update(updatedData);
                }
            }
        });

        this.table.redraw(true);

        this.changedAssignmentsBefore = [];
        this.changedAssignmentsAfter = [];
        this.updateSaveCancelButtons();

        this.hideLoading();
        showToast("Assignment suggest cancelled", "success");
    }

    jumpToWeek() {
        //todo
        const week = getWeekNumber(this.context.store.targetDate);
        const cw = "cw_" + week + "_" + getWeekYear(this.context.store.targetDate);
        var columnField = 'plannedWeeks.' + cw;
        this.table.scrollToColumn(columnField, "middle");
        this.selectCwCell(this.context.store.currentDate, "#ffd0a0");
        this.underlineCwCell(this.context.store.targetDate, "#FFA0A0");
    }

    underlineCwCell(date: Date, color: string) {
        const week = getWeekNumber(date);
        const cw = "cw_" + week + "_" + getWeekYear(date);
        const columns = this.table.getColumns();
        const columnExists = columns.some(column => column.getField() === `plannedWeeks.${cw}`);
        if (columnExists) {
            const column = this.table.getColumn(`plannedWeeks.${cw}`);
            column.getElement().style.borderBottom = '5px solid ' + color;
        }
    }

    selectCwCell(date: Date, color: string) {
        const week = getWeekNumber(date);
        const cw = "cw_" + week + "_" + getWeekYear(date);
        const columns = this.table.getColumns();
        let columnValues = columns.map(column => column.getField());
        console.log(columnValues);
        const columnExists = columns.some(column => column.getField() === `plannedWeeks.${cw}`);
        if (columnExists) {
            const column = this.table.getColumn(`plannedWeeks.${cw}`);
            const element = column.getElement();
            element.style.backgroundColor = color;
        }
    }

    /**
     * Add calendar week columns to the table
     * @param {CalendarWeek[]} cwList array of calendar weeks e.g. {key:"cw_1_2021"}
     * @param {boolean} isEditable
     * @param {{maxHours: number, employeesMap: Map<string, Employee>}} formatterParams
     * @returns {Tabulator.ColumnComponent[]} cwColumns
     */
    addCalendarWeekCols(cwList: CalendarWeek[], isEditable: boolean, formatterParams: { maxHours: number, employeesMap: Map<string, Employee> }) {
        let cwColumns: any[] = [];
        let lastHoveredCell: { edit: () => void; } | undefined = undefined;
        cwList.forEach((cws) => {
            let yearCol = cwColumns.find((c) => c.title === cws.year);

            if (!yearCol) {
                yearCol = {
                    title: cws.year,
                    columns: [],
                    resizable: false
                };
                cwColumns.push(yearCol);
            }

            yearCol.columns.push({
                title: cws.cw,
                field: 'plannedWeeks.' + cws.key,
                width: 75,
                resizable: false,
                formatter: this.cwCellFormatter.bind(this),
                formatterParams: formatterParams,
                headerTooltip: "Calendar Week " + cws.cw + "/" + cws.year + ". " + cws.cw_start.toLocaleDateString() + " - " + cws.cw_end.toLocaleDateString(),
                editable: (cell: CellComponent) => isEditable,
                cellMouseEnter: (e: MouseEvent, cell: CellComponent) => {
                    lastHoveredCell = cell;
                },
                cellMouseLeave: (e: MouseEvent, cell: CellComponent) => {
                    lastHoveredCell = undefined;
                },
                editor: (cell: CellComponent, onRendered: (arg0: () => void) => void, success: (arg0: number) => void, cancel: any) => {
                    let editor = document.createElement("input");
                    editor.style.width = "100%";
                    editor.setAttribute("type", "number");

                    let formattedValue = this.cwCellFormatter(cell);
                    let parentRow = cell.getRow().getTreeParent();
                    let parentData = parentRow ? parentRow.getData() as AssignmentDemand : undefined;
                    
                    let [week, year] = cell.getColumn().getField().split(".")[1].split("_").slice(1).map(Number);
                    let targetDate = this.getUTCWeekDay(year, week, 1);
                    let contractedHours = 40;
                    
                    if (parentData) {
                        parentData.employee?.employmentPeriods.find((period) => {
                            let startDate = period.startDate ? new Date(period.startDate) : new Date(0);
                            let endDate = period.endDate ? new Date(period.endDate) : new Date(9999, 11, 31); 
                            if (startDate <= targetDate && endDate >= targetDate) {
                                contractedHours = period.contractedHours;
                                return true;
                            }
                            return false;
                        });
                    }

                    if (typeof formattedValue === 'string' && formattedValue.endsWith('%'))
                        formattedValue = formattedValue.slice(0, -1);

                    editor.value = formattedValue;

                    let reversedValue: number | null;
                    let successFunc = () => {
                        if (reversedValue)
                            return;

                        reversedValue = this.reverseCwCellFormatter(Number(editor.value), this.unit, contractedHours);
                        if (reversedValue < 0)
                            reversedValue = 0;
                        editor.value = reversedValue.toString();
                        success(reversedValue);
                        if (lastHoveredCell && lastHoveredCell != cell)
                            setTimeout(() => {
                                if (lastHoveredCell)
                                    lastHoveredCell.edit()
                            }, 0);
                    }

                    editor.addEventListener("blur", successFunc);

                    onRendered(() => {
                        editor.focus();
                        editor.style.width = "100%";

                        // Add "%" symbol to the background of the input field
                        if (this.unit == "%") {
                            const getStringWidth = function (string: string, element: HTMLElement) {
                                let canvas = document.createElement('canvas');
                                let context = canvas.getContext('2d');
                                if (context) {
                                    context.font = getComputedStyle(element).font;
                                    return context.measureText(string).width + 5;
                                }
                                return 0;
                            }

                            editor.style.backgroundImage = "url('data:image/svg+xml;utf8,<svg xmlns=\"http://www.w3.org/2000/svg\" width=\"20\" height=\"20\"><text x=\"0\" y=\"15\" fill=\"black\">%</text></svg>')";
                            editor.style.backgroundRepeat = "no-repeat";
                            editor.style.backgroundPosition = `${getStringWidth(formattedValue, editor)}px center`;

                            editor.addEventListener('input', function () {
                                this.style.backgroundPosition = `${getStringWidth(this.value, editor)}px center`;
                            });
                        }
                    });

                    return editor;
                },
                vertAlign: "middle",
                sorter: this.AbscenceOnTopSorter,
            });
        });
        return cwColumns;
    }

    isEditable(cell: CellComponent) {
        let row = cell.getRow();
        let data = row.getData();
        return data.assignments ? false : true;
    }

    buttonFormatter(cell: CellComponent) {
        /** @type {PageContext} */
        const context = globalApp.app.context;

        var bvid = cell.getValue();
        let row = cell.getRow();
        let data = row.getData();

        if (data.assignments && data.role !== AssignmentDemandRole.SuggestionsAssignmentRole) {
            if (data.employeeId === "")
                bvid = "Projects (" + data.assignments.filter((a: AssignmentDemand) => !(a.role === AssignmentDemandRole.AbsenceAssigmentRole || a.role === AssignmentDemandRole.SuggestionsAssignmentRole)).length + ")";

            //add buttons to cell (just the edit and delete buttons to start with)
            var buttonHolder = document.createElement("span");
            buttonHolder.appendChild(document.createTextNode(bvid + " "))

            //create edit button
            if (context.user.hasEditPermission()) {
                var editBt = document.createElement("button");
                editBt.type = "button";
                editBt.textContent = "+";
                editBt.style.lineHeight = "14px";
                editBt.classList.add("btn");
                editBt.classList.add("btn-sm");
                editBt.classList.add("btn-outline-secondary");

                editBt.addEventListener("click", async function (e) {
                    e.stopPropagation();
                    let template = new AssignmentDemand();
                    if (context.user.isAdmin()) {
                        template.employeeId = data.employee.id;
                    } else {
                        template.suggestions = [data.employee.id]
                    }
                    var ctx = Object.assign(context, {
                        data: AssignmentDemand.createNew(template)
                    });
                    await DemandDetailView.showAsModal(ctx);
                });

                buttonHolder.appendChild(editBt);
            }

            return buttonHolder;
        } else {
            //try to resolve project name
            var div = document.createElement("div");
            var projectIdDiv = document.createElement("div");
            projectIdDiv.textContent = bvid;
            div.appendChild(projectIdDiv);
            if (data.projectId && data.projectId !== "") {
                row.getElement().style.height = "auto";
                var projectNameDiv = document.createElement("div");
                context.store.getProject(data.projectId)
                    .then((project) => {
                        if (project) {
                            projectNameDiv.textContent = project.projectName;
                            div.appendChild(projectNameDiv);
                        }
                    });
                div.style.height = "63px";
            }
            return div;
        }
    }

    /**
     * @param {Tabulator.CellComponent} cell
     * @param {{maxHours: number, employeesMap: Map<string, Employee>}} formatterParams 
     * @returns 
     */
    cwCellFormatter(cell: CellComponent) {
        var bvid = cell.getValue();
        let column = cell.getColumn();
        let row = cell.getRow();
        let data = row.getData();
        const parentRow = row.getTreeParent();
        const parentData = parentRow ? parentRow.getData() : undefined;

        /** @type {Employee | undefined} */
        const employee = data.employee.id != undefined
            ? data.employee
            : this.allEmployeesMap.get(data.employeeId);
        let maxHours = employee != undefined && employee.weeklyHours
            ? employee.weeklyHours
            : 40;

        let utilization = this.employeeUtilization.filter(data => employee && employee.id === data.id); 
        let employeeUtilization = utilization.length > 0 ? utilization : undefined;

        if (employeeUtilization) {
            let columnData = column.getField().split("_");
            let currentWeek = Number(columnData[1]);
            let currentYear = Number(columnData[2]);
            let weekUtilisation = utilization.length > 0 ? getEmployeeUtilizationByWeek(utilization[0].utilization, currentWeek, currentYear) : undefined;
            maxHours = weekUtilisation ? weekUtilisation : employee != undefined
                ? employee.weeklyHours
                : 40;
        }

        //smooth color fade cells based on value
        if (bvid !== 0) {
            if (data.role === AssignmentDemandRole.SuggestionsAssignmentRole || parentData?.role === AssignmentDemandRole.SuggestionsAssignmentRole) {
                cell.getElement().style.backgroundColor = percentageToGray(bvid / maxHours);
            }
            else if (data.assignments) {
                cell.getElement().style.backgroundColor = percentageToColorWithInvert(bvid / maxHours);
            } else {
                if (bvid > maxHours) {
                    cell.getElement().style.backgroundColor = "#DF9A9A";
                } else if (bvid > 0) {
                    cell.getElement().style.backgroundColor = "#A6DF9A";
                }
            }
        }
        else {
            cell.getElement().style.backgroundColor = "";
        }

        if (this.unit == "pd") {
            return bvid ? bvid / 8 : 0;
        } else if (this.unit == "%") {
            return (bvid ? Math.round(bvid / maxHours * 100) : 0) + "%";
        }

        return bvid ? (data.role === AssignmentDemandRole.AbsenceAssigmentRole
            ? `<i>${bvid}</i>`
            : bvid) : 0;
    }

    /**
     * @param {string | number} formattedValue
     * @param {string} unit
     * @param {number} maxHours
     * @returns {number}
     */
    reverseCwCellFormatter(formattedValue: number, unit: string, maxHours: number) {
        let bvid;

        if (unit == "pd") {
            bvid = formattedValue * 8;
        } else if (unit == "%") {
            bvid = formattedValue / 100 * maxHours;
        } else {
            bvid = formattedValue;
        }

        return Math.round(bvid);
    }

    /**
     * Create circles with color based on value and fist letters of names
     * @param {Tabulator.CellComponent} cell
     * @param {object} formatterParams
     * @returns {string}
    */
    namesWithIconFormatter(cell: CellComponent) {
        var bvid = cell.getValue();
        let row = cell.getRow();
        let data = row.getData();
        const parentRow = row.getTreeParent();
        const parentData = parentRow ? parentRow.getData() : undefined;

        if (data.assignments && data.assignments.length > 0 && data.role !== AssignmentDemandRole.SuggestionsAssignmentRole) {
            var name = data.employee.employeeName;
            const iconHtml = data.employee && data.employee.photo
                ? createEmployeePhotoElement(data.employee.photo, "name-cell-icon mx-2")
                : createNameIconElementWith2Initials(name, "name-cell-icon mx-2 p-1");

            const textHtml = `<span>${bvid}</span>`;

            const container = document.createElement("span");
            container.appendChild(convertHtmlToNode(iconHtml));
            container.appendChild(convertHtmlToNode(textHtml));
            container.addEventListener("click", (event) => {
                event.stopPropagation(); // Avoid expand/collapse
                showResourceDetailModal(data.employee, globalApp.app.context);
            });

            return container;
        } else if (data.role === AssignmentDemandRole.AbsenceAssigmentRole) {
            return `<i>${bvid}</i>`
        }
        else if (data.role === AssignmentDemandRole.SuggestionsAssignmentRole || parentData?.role === AssignmentDemandRole.SuggestionsAssignmentRole) {
            row.getElement().classList.add('suggested-assignment-row');
            return `<i>${bvid}</i>`
        }
        else {
            //role
            if (data.role === undefined || data.role === "") bvid = "<Undefined role>";
            return `<span>${bvid}</span>`;
        }
    }

    applyFilteredView() {
        let filter = this.filteredViews[this.filteredView];
        this.table.setFilter((data: any) => !!filter(data));
        this.setInitialTableSort();
        this.resetTableScroll();
        this.context.params.filteredView = this.filteredView;
        Navigation.navigateHash(null, this.context.params, true);
    }

    async calculateRowsForEachFilter() {
        Object.keys(this.filteredViews).forEach((filterName) => {
            let filter = this.filteredViews[filterName as keyof typeof this.filteredViews];
            let rows = this.assignments.filter(filter);
            if (filterName === "me") {
                $(this).find(`.btn-filteredViews button[data-view='${filterName}']`)
                    .text(`${filterName}`);
            } else {
                $(this).find(`.btn-filteredViews button[data-view='${filterName}']`)
                    .text(`${filterName} (${rows.length})`);
            }
        });
    }

    setInitialTableSort() {
        if (!this.table.setSort) return;
        this.table.setSort([
            { column: "role", dir: "asc" }
        ]);
    }

    resetTableScroll() {
        if (this.table == undefined) return;
        const firstRow = this.table.getRowFromPosition(1);
        if (firstRow) firstRow.scrollTo();
    }


    customOriginatingProjectFilter(headerValue: string, _rowValue: RowComponent, rowData: any) {
        var keepItem = false;
        var filterValue = replaceAccentedCharacters(headerValue.toLowerCase().replace(/\s/g, ''));
        var filterValue = headerValue.toLowerCase().replace(/\s/g, '');
        if (!rowData.originatingProjectId)
            keepItem = false;
        else {
            if (rowData.assignments) {
                rowData.assignments.forEach((assignment: AssignmentDemand) => {
                    if (!assignment.originatingProjectId) return;
                    var originatingProject = replaceAccentedCharacters(assignment.originatingProjectId.toLowerCase().replace(/\s/g, ''));
                    if (originatingProject.length > 0 && originatingProject.indexOf(filterValue) !== -1) {
                        keepItem = true;
                    }
                });
            }
            else {
                var originatingProject = replaceAccentedCharacters(rowData.originatingProjectId.toLowerCase().replace(/\s/g, ''));
                if (originatingProject.length > 0 && originatingProject.indexOf(filterValue) !== -1) {
                    keepItem = true;
                }
            }
        }
        return keepItem;
    }

    subscribedAssignmentChangeHandler = async (assignment: AssignmentDemand, type: string) => {
        let rows = this.table.getRows();
        let row = rows.find(row => {
            let data = row.getData();
            return data.assignments.some((a: AssignmentDemand) => a.id === assignment.id);
        });
        
        //new suggested assignment
        let rowsForSuggestedAssignment : RowComponent[] = [];
        //old suggested assignment
        let suggestedAsignmentRows = rows.filter(row => {
            const data = row.getData();
            if (assignment.suggestions.includes(data.employee.id)) {
                rowsForSuggestedAssignment.push(row);
            }
            return data.assignments?.some((value: AssignmentDemand) =>
                value.role === AssignmentDemandRole.SuggestionsAssignmentRole &&
                value.assignments?.some((a: AssignmentDemand) => a.id === assignment.id)
            );
        });

        let expandedRows : RowComponent[] = [];
        //save rows that are expanded and have expanded children
        function writeExpandedRows(row: RowComponent) {
            if (row.isTreeExpanded()){
                row.getTreeChildren().forEach((childRow) => {
                    if (childRow.isTreeExpanded() && !expandedRows.includes(row))
                        expandedRows.push(row)
                });
            }
        }

        if (rowsForSuggestedAssignment.length > 0 || suggestedAsignmentRows.length > 0) {
            suggestedAsignmentRows.forEach(async row => {
                let data = row.getData() as AssignmentDemand;
                if (data.assignments) {
                    data.assignments.filter((a: AssignmentDemand) => {
                        if (a.role === AssignmentDemandRole.SuggestionsAssignmentRole) {
                            a.assignments = a.assignments ? a.assignments.filter((a: AssignmentDemand) => a.id != assignment.id) : [];
                            if (a.assignments && a.assignments.length == 0 && data.assignments) {
                                data.assignments = data.assignments.filter((a: AssignmentDemand) => a.role !== AssignmentDemandRole.SuggestionsAssignmentRole);
                            }
                        }
                    });
                }

                writeExpandedRows(row);
                await row.update(data);

            });
            let validSuggestion = assignment.status != "Dropped" && assignment.status != "Lost" && assignment.status != "Resolved"
            if (validSuggestion && (type === "modified" || type === "added")) {
                rowsForSuggestedAssignment.forEach(row => {
                    let data = row.getData() as AssignmentDemand;
                    if (data.assignments) {
                        let suggestionAssignment = data.assignments.find((a: AssignmentDemand) => a.role === AssignmentDemandRole.SuggestionsAssignmentRole);
                        if (!suggestionAssignment)
                        {
                            suggestionAssignment = this.context.store.createSuggestionsAssigment(data.employee ?? new Employee(), []);
                            data.assignments.push(suggestionAssignment);
                        }
                        suggestionAssignment.assignments = suggestionAssignment.assignments?.filter((a: AssignmentDemand) => a.id != assignment.id) || [];
                        suggestionAssignment.assignments?.push(assignment);
                    }

                    writeExpandedRows(row);
                    row.update(data);
                });
            }
        }

        if (!row) {
            row = rows.find(row => {
                let data = row.getData();
                return data.employee.id === assignment.employeeId;
            });
        }
        if (!row) return;

        if (type === "modified" || type === "added") {
            let data = row.getData();

            //changed assigned employee
            if (assignment.employeeId != data.employee.id) {
                let newParentRow = rows.find(row => {
                    let data = row.getData();
                    return data.employee.id === assignment.employeeId;
                });
                
                data.assignments = data.assignments.filter((a: AssignmentDemand) => a.id !== assignment.id);
                let calculatedAssignment = await this.context.store.calculateAssignmentWeeklyHours(assignment);

                if (newParentRow)
                {
                    let newParentData = newParentRow.getData() as AssignmentDemand;
                    if (newParentData.assignments)
                        newParentData.assignments.push(calculatedAssignment);
                    this.context.store.calculateTotalWeekly(newParentData);
                    newParentRow.update(newParentData);
                    newParentRow.reformat();
                }
            }
            else{
                let calculatedAssignment = await this.context.store.calculateAssignmentWeeklyHours(assignment);

                if (data.assignments.some((a: AssignmentDemand) => a.id === calculatedAssignment.id)) 
                    data.assignments = data.assignments.map((a: AssignmentDemand) => a.id === calculatedAssignment.id ? calculatedAssignment : a);
                else
                    data.assignments.push(calculatedAssignment);
    
                data.assignments = data.assignments.filter((a: AssignmentDemand) => {
                    if (a.id == calculatedAssignment.id)
                        return a.status == "Resolved"
                    return true;
                });

                this.context.store.calculateTotalWeekly(data);
            }

            writeExpandedRows(row);
            await row.update(data);
            console.log("assignment modified by other user");
        }
        else if (type === "removed") {
            let data = row.getData();
            data.assignments = data.assignments.filter((a: AssignmentDemand) => a.id !== assignment.id);
            this.context.store.calculateTotalWeekly(data);
            writeExpandedRows(row);
            await row.update(data);
            console.log("assignment removed by other user");
        }

        row.reformat();
        expandedRows.forEach(row => {
            const updatedRow = this.table.getRow(row.getIndex());
            updatedRow.treeExpand();
            updatedRow.getTreeChildren().forEach((childRow) => childRow.treeExpand());
        });
    }
}